import {
  EXCEPTION_STATUS_LABEL,
  cisLevelOf,
  type ScaCheck,
  type ScaCheckResult,
  type ServerHardening,
} from '@bartizo/shared';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { getServerCompliance, getServerHardening } from '../api/servers.js';
import { useCurrentUser } from '../auth/session-context.js';
import { FixSteps } from './FixSteps.js';
import { DecisionBadge, GuidanceNote, RecordDecision } from './ComplianceDecisions.js';
import { useRemoveDecision } from './use-remove-decision.js';
import { formatInstant, formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Card } from '../ui/Card.js';
import { ShareBar } from '../ui/DataTable.js';

/**
 * A server's hardening, as its Wazuh agent assessed it and the central manager
 * holds it (P11.8.6): each SCA policy's score, every check — a failing one with
 * why it matters and how to fix it — and rootcheck's findings. Read by the API
 * every ten minutes; Wazuh scans every twelve hours and when the agent starts.
 */

const RESULT_BADGE: Readonly<Record<ScaCheckResult, string>> = {
  failed: 'badge-danger',
  passed: 'badge-success',
  'not applicable': 'badge-muted',
};

const SHOWN: readonly { value: ScaCheckResult | 'all'; label: string }[] = [
  { value: 'failed', label: 'Failing' },
  { value: 'not applicable', label: 'Not applicable' },
  { value: 'passed', label: 'Passing' },
  { value: 'all', label: 'All' },
];

export function HardeningTab({
  serverId,
  timezone,
}: {
  serverId: string;
  timezone: string;
}): ReactNode {
  const hardening = useQuery({
    queryKey: ['hardening', serverId],
    queryFn: () => getServerHardening(serverId),
    refetchInterval: 5 * 60_000,
  });

  if (hardening.isPending) {
    return <div className="skeleton skeleton-block" aria-busy="true" aria-label="Loading" />;
  }
  if (hardening.isError) {
    return (
      <Alert tone="danger" title="Could not load what Wazuh found">
        {hardening.error instanceof Error ? hardening.error.message : 'Try again in a moment.'}
      </Alert>
    );
  }
  const data = hardening.data;
  if (data.readAt === null && data.policies.length === 0) {
    return (
      <Card title="Hardening">
        <p className="muted text-sm">
          Nothing read yet. The central server reads what this server's Wazuh agent found — its
          configuration assessment against the CIS benchmark and rootcheck's findings — every ten
          minutes once the agent reports to it. A server whose Wazuh runs locally has none here.
        </p>
      </Card>
    );
  }
  return (
    <>
      <ComplianceCard serverId={serverId} />
      {data.policies.map((policy) => (
        <PolicyCard
          key={policy.policyId}
          policy={policy}
          data={data}
          timezone={timezone}
          serverId={serverId}
        />
      ))}
      {data.policies.length === 0 && (
        <Card title="Configuration assessment">
          <p className="muted text-sm">
            The agent has no assessment policy for this system, so there is nothing to show.
          </p>
        </Card>
      )}
      <RootcheckCard rootcheck={data.rootcheck} timezone={timezone} />
    </>
  );
}

/** P11.8.9: this server's score per standard, each opening the fleet's view of it. */
function ComplianceCard({ serverId }: { serverId: string }): ReactNode {
  const scores = useQuery({
    queryKey: ['server-compliance', serverId],
    queryFn: () => getServerCompliance(serverId),
  });
  if (scores.data === undefined || scores.data.length === 0) return null;
  return (
    <Card
      title="Compliance"
      description="Each standard its checks are mapped to: the score is the passing share of its checks; a control fails when any of its checks fails. Evidence from the benchmark, not an audit."
    >
      <dl className="stat-tiles">
        {scores.data.map((each) => (
          <div
            key={each.standard}
            className={`stat-tile ${each.failing > 0 ? 'stat-tile-alert' : ''}`}
          >
            <dt>
              <Link to={`/compliance?standard=${encodeURIComponent(each.standard)}`}>
                {each.label}
              </Link>
            </dt>
            <dd>{each.score === null ? '—' : `${String(each.score)}%`}</dd>
            <dd className="cell-sub">
              {each.failing} of {each.failing + each.passing} controls failing
            </dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

function PolicyCard({
  policy,
  data,
  timezone,
  serverId,
}: {
  policy: ServerHardening['policies'][number];
  data: ServerHardening;
  timezone: string;
  serverId: string;
}): ReactNode {
  const [shown, setShown] = useState<ScaCheckResult | 'all'>(policy.failed > 0 ? 'failed' : 'all');
  const checks = data.checks.filter(
    (check) => check.policyId === policy.policyId && (shown === 'all' || check.result === shown),
  );
  return (
    <Card
      title={policy.name}
      description={`Scanned ${formatRelative(policy.scannedAt)} (${formatInstant(policy.scannedAt, { timezone })}). The score is the passing share of the checks that could be judged.`}
      aside={
        <span
          className={`badge ${policy.score >= 80 ? 'badge-success' : policy.score >= 50 ? 'badge-warning' : 'badge-danger'}`}
        >
          Score {policy.score}%
        </span>
      }
    >
      <dl className="stat-tiles">
        <div className={`stat-tile ${policy.failed > 0 ? 'stat-tile-alert' : ''}`}>
          <dt>Failing</dt>
          <dd>{policy.failed}</dd>
        </div>
        <div className="stat-tile">
          <dt>Passing</dt>
          <dd>{policy.passed}</dd>
        </div>
        <div className="stat-tile">
          <dt>Not applicable</dt>
          <dd>{policy.notApplicable}</dd>
        </div>
      </dl>
      <ShareBar value={policy.passed} total={policy.passed + policy.failed} />

      <div className="segmented" role="group" aria-label="Which checks">
        {SHOWN.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={shown === option.value}
            onClick={() => {
              setShown(option.value);
            }}
          >
            {option.label}
          </button>
        ))}
      </div>

      {checks.length === 0 ? (
        <p className="muted text-sm">None.</p>
      ) : (
        <ul className="check-list">
          {checks.map((check) => (
            <li key={check.id}>
              <CheckRow check={check} timezone={timezone} serverId={serverId} />
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function CheckRow({
  check,
  timezone,
  serverId,
}: {
  check: ScaCheck;
  timezone: string;
  serverId: string;
}): ReactNode {
  const isAdmin = useCurrentUser().user.role === 'admin';
  const removal = useRemoveDecision();
  const cis = check.compliance['cis']?.[0];
  const decided = check.exception;
  const level = cisLevelOf(check.title);
  // Only a CIS policy's checks have a level; one whose level is not known says so.
  const levelText =
    cis === undefined
      ? undefined
      : level === null
        ? 'level not known'
        : level === 1
          ? 'Level 1'
          : level === 2
            ? 'Level 2'
            : 'workstation only';
  const head = (
    <>
      <span className={`badge ${RESULT_BADGE[check.result]}`}>{check.result}</span>{' '}
      <span>{check.title}</span>
      {decided?.active === true && (
        <>
          {' '}
          <span className="badge badge-muted">{EXCEPTION_STATUS_LABEL[decided.status]}</span>
        </>
      )}
      <span className="cell-sub">
        {cis !== undefined && `CIS ${cis}`}
        {levelText !== undefined && ` · ${levelText}`}
        {check.failingSince !== null && (
          <span title={formatInstant(check.failingSince, { timezone })}>
            {cis !== undefined && ' · '}failing since {formatRelative(check.failingSince)}
          </span>
        )}
      </span>
    </>
  );
  if (check.result !== 'failed') {
    return (
      <div className="check-row">
        {head}
        {check.reason !== null && <span className="cell-sub">{check.reason}</span>}
      </div>
    );
  }
  return (
    <details className="check-row">
      <summary>{head}</summary>
      {check.rationale !== null && (
        <section>
          <h4>Why it matters</h4>
          <p className="text-sm">{check.rationale}</p>
        </section>
      )}
      {check.remediation !== null && (
        <section>
          <h4>How to fix it</h4>
          <FixSteps title={check.title} remediation={check.remediation} />
        </section>
      )}
      {check.subject !== null && (
        <section>
          <h4>What was checked</h4>
          <code className="mono text-sm">{check.subject}</code>
        </section>
      )}
      <GuidanceNote title={check.title} />
      {decided !== null && (
        <DecisionBadge
          exception={decided}
          timezone={timezone}
          onRemove={
            isAdmin && !removal.pending
              ? () => {
                  removal.remove(decided.id);
                }
              : undefined
          }
        />
      )}
      {isAdmin && decided?.active !== true && (
        <RecordDecision
          target={{ policyId: check.policyId, checkId: check.id, title: check.title }}
          scope={{ server: serverId }}
          scopeName="this server"
        />
      )}
      <Standards check={check} />
    </details>
  );
}

/** The standards a check maps to, for P11.8.9's views and an auditor's questions. */
function Standards({ check }: { check: ScaCheck }): ReactNode {
  const entries = Object.entries(check.compliance).filter(
    ([key]) => !key.startsWith('mitre_') && key !== 'cis',
  );
  if (entries.length === 0) return null;
  return (
    <p className="cell-sub">
      {entries.map(([key, values]) => `${key.replace(/_/g, ' ')} ${values.join(', ')}`).join(' · ')}
    </p>
  );
}

function RootcheckCard({
  rootcheck,
  timezone,
}: {
  rootcheck: ServerHardening['rootcheck'];
  timezone: string;
}): ReactNode {
  const outstanding = rootcheck.findings.filter((finding) => finding.status === 'outstanding');
  return (
    <Card
      title="Rootcheck"
      description={
        rootcheck.scannedAt === null
          ? 'Signs of a rootkit or a tampered system: hidden files and processes, trojaned binaries.'
          : `Signs of a rootkit or a tampered system, as the scan ${formatRelative(rootcheck.scannedAt)} found them.`
      }
      aside={
        outstanding.length === 0 ? (
          <span className="badge badge-success">Nothing found</span>
        ) : (
          <span className="badge badge-warning">
            {outstanding.length === 1 ? '1 finding' : `${String(outstanding.length)} findings`}
          </span>
        )
      }
    >
      {rootcheck.findings.length === 0 ? (
        <p className="muted text-sm">Its last scan found nothing.</p>
      ) : (
        <ul className="check-list">
          {rootcheck.findings.map((finding) => (
            <li key={finding.title} className="check-row">
              <span
                className={`badge ${finding.status === 'outstanding' ? 'badge-warning' : 'badge-muted'}`}
              >
                {finding.status}
              </span>{' '}
              <span>{finding.title}</span>
              <span className="cell-sub" title={formatInstant(finding.firstSeenAt, { timezone })}>
                first seen {formatRelative(finding.firstSeenAt)}
                {finding.lastSeenAt !== finding.firstSeenAt &&
                  ` · last ${formatRelative(finding.lastSeenAt)}`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
